"use client";

import { motion } from "framer-motion";
import { ArrowRight, Send } from "lucide-react";
import { type FormEvent, useState } from "react";

import { FadeIn } from "@/components/ui/motion";
import { useLang, useLangKey, useLangPrefix } from "@/contexts/lang";
import type { SectionIds } from "@/data/home";
import { submitContact } from "@/lib/submitContact";

type ContactSectionProps = {
	sectionIds: SectionIds;
};

export function ContactSection({ sectionIds }: ContactSectionProps) {
	const t = useLang();
	const lang = useLangKey();
	const prefix = useLangPrefix();

	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [message, setMessage] = useState("");
	const [attempted, setAttempted] = useState(false);

	async function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setAttempted(true);
		if (!name || !email || !message) return;

		setStatus("loading");
		try {
			await submitContact({
				data: { name, email, message },
			});
			setStatus("success");
		} catch (err) {
			console.error("Failed to submit contact section form:", err);
			setStatus("error");
		}
	}

	const req = lang === "en" ? "Required" : "Задолжително";
	const nameErr = attempted && !name;
	const emailErr = attempted && !email;
	const messageErr = attempted && !message;

	const inputBase =
		"w-full px-5 py-3.5 rounded-sm border bg-white/5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 transition-all text-sm";
	const inputOk = "border-white/10 focus:ring-accent/50 focus:border-accent/50";
	const inputErr = "border-destructive ring-1 ring-destructive/50";

	return (
		<section
			id={sectionIds.contact}
			className="scroll-mt-32 bg-cream pb-16 sm:pb-20 md:pb-28 mt-paper"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Elegant divider */}
				<div className="flex items-center gap-4 py-12 sm:py-16 md:py-20">
					<span className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
				</div>

				<FadeIn>
					<div className="overflow-hidden rounded-sm bg-primary text-white relative shadow-luxury-xl mt-grain">
						{/* Refined ambient background */}
						<div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent" />

						<motion.div
							className="absolute top-0 right-0 w-[280px] sm:w-[400px] md:w-[500px] h-[280px] sm:h-[400px] md:h-[500px] rounded-full pointer-events-none"
							style={{
								background:
									"radial-gradient(closest-side, rgba(201, 161, 90, 0.08), transparent)",
							}}
							animate={{
								scale: [1, 1.15, 1],
								opacity: [0.5, 0.7, 0.5],
							}}
							transition={{
								duration: 12,
								repeat: Number.POSITIVE_INFINITY,
								ease: "easeInOut",
							}}
						/>

						<motion.div
							className="absolute bottom-0 left-0 w-[300px] md:w-[400px] h-[300px] md:h-[400px] rounded-full pointer-events-none"
							style={{
								background:
									"radial-gradient(closest-side, rgba(255, 255, 255, 0.04), transparent)",
							}}
							animate={{
								scale: [1, 1.1, 1],
								x: [0, 15, 0],
							}}
							transition={{
								duration: 15,
								repeat: Number.POSITIVE_INFINITY,
								ease: "easeInOut",
							}}
						/>

						{/* Decorative corner */}
						<div className="absolute top-0 right-0 w-24 h-24 pointer-events-none opacity-40">
							<svg
								viewBox="0 0 96 96"
								fill="none"
								className="w-full h-full"
								aria-hidden="true"
							>
								<path
									d="M96 0 L96 96 L0 96"
									stroke="rgba(201, 161, 90, 0.3)"
									strokeWidth="1"
									fill="none"
								/>
							</svg>
						</div>

						<div className="grid gap-10 md:gap-14 p-6 sm:p-8 md:p-12 lg:p-16 lg:grid-cols-2 relative z-10">
							{/* Left - Form */}
							<div>
								<FadeIn delay={0.1}>
									{/* Label */}
									<div className="flex items-center gap-4 mb-6">
										<span className="h-px w-10 sm:w-12 bg-accent/60" />
										<p className="text-label text-accent">
											{t.contactSection.label}
										</p>
									</div>

									<h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-normal text-white leading-[1.1] tracking-tight mb-5 md:mb-6">
										{t.contactSection.heading}
									</h2>

									<p className="text-refined text-base sm:text-lg text-white/60 mb-8 md:mb-10 max-w-md">
										{t.contactSection.intro}
									</p>
								</FadeIn>

								<FadeIn delay={0.2}>
									{/* Form */}
									{status === "success" ? (
										<div className="py-12 flex flex-col items-center justify-center text-center gap-4 border border-white/10 rounded-sm bg-white/5">
											<div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
												<Send className="h-6 w-6 text-accent" />
											</div>
											<h3 className="font-display text-xl font-medium text-white">
												{lang === "en"
													? "Message Sent!"
													: "Пораката е испратена!"}
											</h3>
											<p className="text-white/60 text-sm max-w-[240px]">
												{lang === "en"
													? "Thank you for reaching out. We will get back to you soon."
													: "Ви благодариме. Ќе ве контактираме наскоро."}
											</p>
											<button
												type="button"
												onClick={() => {
													setStatus("idle");
													setAttempted(false);
													setName("");
													setEmail("");
													setMessage("");
												}}
												className="mt-2 text-xs font-semibold text-accent hover:text-accent-light transition-colors"
											>
												{lang === "en"
													? "Send another message"
													: "Испрати друга порака"}
											</button>
										</div>
									) : (
										<form
											onSubmit={handleSubmit}
											noValidate
											className="space-y-4"
										>
											<div>
												<input
													type="text"
													name="name"
													value={name}
													onChange={(e) => setName(e.target.value)}
													placeholder={t.contactSection.namePlaceholder}
													className={`${inputBase} ${nameErr ? inputErr : inputOk}`}
												/>
												{nameErr && (
													<p className="text-destructive text-[10px] mt-1">
														{req}
													</p>
												)}
											</div>
											<div>
												<input
													type="email"
													name="email"
													value={email}
													onChange={(e) => setEmail(e.target.value)}
													placeholder={t.contactSection.emailPlaceholder}
													className={`${inputBase} ${emailErr ? inputErr : inputOk}`}
												/>
												{emailErr && (
													<p className="text-destructive text-[10px] mt-1">
														{req}
													</p>
												)}
											</div>

											<div>
												<textarea
													name="message"
													value={message}
													onChange={(e) => setMessage(e.target.value)}
													rows={4}
													placeholder={t.contactSection.messagePlaceholder}
													className={`${inputBase} ${messageErr ? inputErr : inputOk} min-h-[130px] resize-none`}
												/>
												{messageErr && (
													<p className="text-destructive text-[10px] mt-1">
														{req}
													</p>
												)}
											</div>

											<button
												type="submit"
												disabled={status === "loading"}
												className="w-full rounded-sm bg-accent px-6 py-3.5 text-sm font-semibold text-primary transition-all duration-500 shadow-gold hover:shadow-gold-lg hover:bg-accent-light cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
											>
												{status === "loading"
													? lang === "en"
														? "Sending..."
														: "Се испраќа..."
													: t.contactSection.submit}
											</button>
											{status === "error" && (
												<p className="text-center text-xs text-destructive">
													{lang === "en"
														? "Something went wrong. Please try again."
														: "Се случи грешка. Обидете се повторно."}
												</p>
											)}
										</form>
									)}

									<div className="mt-8 pt-6 border-t border-white/10">
										<a
											href={`${prefix}/contact`}
											className="group inline-flex items-center gap-3 text-sm font-medium text-white/50 hover:text-white transition-colors cursor-pointer"
										>
											<span>{t.contactSection.moreOptions}</span>
											<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
										</a>
									</div>
								</FadeIn>
							</div>

							{/* Right - Office info */}
							<div className="flex flex-col justify-center">
								<FadeIn direction="left" delay={0.2}>
									<div className="rounded-sm bg-white/[0.03] p-6 sm:p-8 border border-white/[0.06] backdrop-blur-sm">
										{/* Decorative line */}
										<div className="w-10 h-px bg-accent/40 mb-6" />

										<h3 className="font-display text-2xl sm:text-3xl font-normal text-white mb-6 tracking-tight">
											{t.contactSection.officeHeading}
										</h3>

										<div className="space-y-5 text-sm">
											<div>
												<span className="text-label text-white/60 block mb-2">
													{t.contactSection.addressLabel}
												</span>
												<p className="text-white/85 leading-relaxed">
													{t.contactSection.addressValue
														.split("\n")
														.map((line, i, arr) => (
															<span key={line}>
																{line}
																{i < arr.length - 1 ? <br /> : null}
															</span>
														))}
												</p>
											</div>
											<div>
												<span className="text-label text-white/60 block mb-2">
													{t.contactSection.phoneLabel}
												</span>
												<a
													href="tel:+38923292731"
													className="text-white/85 hover:text-accent transition-colors cursor-pointer"
												>
													+389 2 3292 731
												</a>
											</div>
											<div>
												<span className="text-label text-white/60 block mb-2">
													{t.contactSection.emailLabel}
												</span>
												<a
													href="mailto:maktur@makedonijaturist.com.mk"
													className="text-white/85 hover:text-accent transition-colors break-all cursor-pointer"
												>
													maktur@makedonijaturist.com.mk
												</a>
											</div>
										</div>

										<div className="mt-8 pt-6 border-t border-white/[0.06]">
											<p className="text-[10px] text-white/45 uppercase tracking-[0.2em]">
												Makedonijaturist AD / {new Date().getFullYear()}
											</p>
										</div>
									</div>
								</FadeIn>
							</div>
						</div>
					</div>
				</FadeIn>
			</div>
		</section>
	);
}
